import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Car, Menu, X, Languages } from "lucide-react";
import { useLang, scrollToId } from "@/lang";

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav?.services },
    { id: "booking", label: t.nav?.booking },
    { href: "/tarifs", label: t.nav?.tarifs },
    { id: "testimonials", label: t.nav?.testimonials },
    { id: "contact", label: t.nav?.contact },
  ];

  const go = (link) => {
    setOpen(false);
    if (link.href) { window.location.href = link.href; return; }
    if (window.location.pathname !== "/") {
      sessionStorage.setItem("scrollTo", link.id);
      window.location.href = "/";
      return;
    }
    scrollToId(link.id);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled ? "bg-obsidian/80 backdrop-blur-xl border-b border-amber-500/10" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
        <button data-testid="brand-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3 group">
          <img src="/logo-badge.png" alt="Kenny Cab" className="w-10 h-10 rounded-full object-cover border border-amber-600/40 group-hover:shadow-[0_0_20px_rgba(154,123,36,0.3)] transition-shadow duration-300" />
          <span className="font-serif text-lg tracking-wide text-ivory">Kenny <span className="italic text-champagne">Cab</span></span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id || l.href}
              data-testid={`nav-link-${l.id || "tarifs"}`}
              onClick={() => go(l)}
              className="text-xs uppercase tracking-[0.2em] text-zinc-600 hover:text-champagne-bright transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle-btn"
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-champagne border border-amber-500/20 hover:border-amber-400/60 rounded-full px-3 py-1.5 transition-colors duration-300"
          >
            <Languages size={13} />
            {lang === "fr" ? "EN" : "FR"}
          </button>
          <button
            data-testid="nav-booking-cta"
            onClick={() => go("booking")}
            className="hidden sm:block text-xs uppercase tracking-[0.2em] bg-champagne text-obsidian font-semibold rounded-full px-5 py-2.5 hover:bg-champagne-bright hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all duration-300"
          >
            {t.nav?.cta}
          </button>
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="md:hidden text-ivory p-2">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-obsidian/95 backdrop-blur-xl border-b border-amber-500/10 overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {links.map((l) => (
                <button key={l.id || l.href} data-testid={`mobile-nav-${l.id}`} onClick={() => go(l)} className="text-left text-sm uppercase tracking-[0.2em] text-zinc-700 hover:text-champagne transition-colors">
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
